import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import { showToast } from "@/other/utils/utils.jsx";
import type { BoardContext } from "@/bryntum-lib/models/BoardContext";
import { duplicateBoardContextRecords } from "../utils/duplicateBoardContext";
import { recordHasNonPreviewGroups } from "./contextGroupUtils";
import { useAsyncTaskHandler } from "./BoardContextTaskHandler";

export type ModalMode = "create" | "resume" | "extend";

interface ContextPageContextValue {
  contexts: BoardContext[];
  activeContext: BoardContext | null;
  listSelectionId: number | null;
  panelContextId: number | null;
  modalContextId: number | null;
  modalOpen: boolean;
  modalMode: ModalMode | null;
  selectContext: (id: number) => void;
  deleteContext: (id: number) => Promise<void>;
  renameContext: (id: number, name: string) => Promise<void>;
  makeActive: (id: number) => Promise<void>;
  duplicateContext: (id: number) => Promise<number | null>;
  isMakingActive: boolean;
  isDuplicating: boolean;
  isCreatingNew: boolean;
  newWizardKey: string | null;
  modalSessionKey: string | null;
  beginNewContext: () => void;
  openAddContextExtend: (targetId: number) => Promise<void>;
  openModalForContext: (id: number, mode?: ModalMode) => void;
  duplicateContextForExtend: (sourceId: number) => Promise<number>;
  finalizeExtendDuplicate: (newId: number) => void;
  hideModal: () => void;
  handoffModalToPanel: (finishedId: number) => void;
  commitNewWizardContext: (id: number) => void;
  cancelNewContext: () => void;
}

const ContextPageCtx = createContext<ContextPageContextValue | undefined>(undefined);

const resolveDefaultPanelContextId = (contexts: BoardContext[]): number | null => {
  const active = contexts.find((c) => c.isActive && c.status === "completed");
  if (active) return active.id;
  const firstCompleted = contexts.find((c) => c.status === "completed");
  return firstCompleted?.id ?? null;
};

export const ContextPageProvider = ({ children }: { children: ReactNode }) => {
  const { project, selectedBoard } = useSwivuBryntumContext();
  const boardId = useMemo(() => selectedBoard?.id, [selectedBoard]);
  const { version: boardContextVersion } = useSwivuStore({ storeId: STORE_KEYS.boardContextStore });
  const { getPhase } = useAsyncTaskHandler();

  const [listSelectionId, setListSelectionId] = useState<number | null>(null);
  const [panelContextId, setPanelContextId] = useState<number | null>(null);
  const [modalContextId, setModalContextId] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode | null>(null);
  const [isMakingActive, setIsMakingActive] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newWizardKey, setNewWizardKey] = useState<string | null>(null);
  const [extendWizardKey, setExtendWizardKey] = useState<string | null>(null);

  const contexts = useMemo<BoardContext[]>(() => {
    if (!project) return [];
    const store = project.getCrudStore(STORE_KEYS.boardContextStore);
    if (!store) return [];
    const all = (store.records as unknown as BoardContext[]).filter(
      (r) => r.board === boardId || r.board === Number(boardId)
    );
    const rank = (c: BoardContext) => {
      if (c.isActive) return 0;
      if (c.activatedAt == null) return 1;
      return 2;
    };
    return [...all].sort((a, b) => {
      const diff = rank(a) - rank(b);
      if (diff !== 0) return diff;
      const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      return bTime - aTime;
    });
  }, [project, boardContextVersion, boardId]);

  const activeContext = useMemo(
    () => contexts.find((c) => c.isActive) ?? null,
    [contexts]
  );

  useEffect(() => {
    if (panelContextId != null) return;
    const defaultId = resolveDefaultPanelContextId(contexts);
    if (defaultId != null) {
      setPanelContextId(defaultId);
      setListSelectionId((prev) => prev ?? defaultId);
    }
  }, [contexts, panelContextId]);

  const modalSessionKey = useMemo(() => {
    if (newWizardKey) return newWizardKey;
    if (extendWizardKey) return extendWizardKey;
    if (modalContextId != null) return `modal-${modalContextId}`;
    if (modalMode === "create" && modalOpen) return `create-${Date.now()}`;
    return null;
  }, [newWizardKey, extendWizardKey, modalContextId, modalMode, modalOpen]);

  const selectContext = useCallback(
    (id: number) => {
      const record = contexts.find((c) => c.id === id);
      if (!record) return;

      const hasNonPreviewGroups = recordHasNonPreviewGroups(project, record.id);
      const hasPanelContent =
        record.status === "completed" || (record.status === "in_progress" && hasNonPreviewGroups);

      const taskPhase = getPhase(id);
      const hasActiveWork = taskPhase !== "idle";

      const needsUserAction =
        record.status === "in_progress" &&
        record.draftQuestions !== null &&
        Array.isArray(record.draftQuestions?.questions) &&
        (record.draftQuestions.questions.length ?? 0) > 0;

      if (hasPanelContent && (record.status === "completed" || (!hasActiveWork && !needsUserAction))) {
        setListSelectionId(id);
        setPanelContextId(id);
        setModalOpen(false);
        setModalContextId(null);
        setModalMode(null);
        return;
      }

      if (hasPanelContent && (hasActiveWork || needsUserAction)) {
        setListSelectionId(id);
        setPanelContextId(id);
        setModalOpen(true);
        setModalContextId(id);
        setModalMode("resume");
        setNewWizardKey(null);
        setExtendWizardKey(null);
        setIsCreatingNew(false);
        return;
      }

      // No panel content — open modal without changing panel
      setListSelectionId(listSelectionId);
      setModalOpen(true);
      setModalContextId(id);
      setModalMode("resume");
      setNewWizardKey(null);
      setExtendWizardKey(null);
      setIsCreatingNew(false);
    },
    [contexts, project, listSelectionId, getPhase]
  );

  const beginNewContext = useCallback(() => {
    setNewWizardKey(`new-${Date.now()}`);
    setExtendWizardKey(null);
    setIsCreatingNew(true);
    setListSelectionId(null);
    setModalContextId(null);
    setModalMode("create");
    setModalOpen(true);
  }, []);

  const commitNewWizardContext = useCallback((id: number) => {
    setModalContextId(id);
    setListSelectionId(id);
    setIsCreatingNew(false);
  }, []);

  const cancelNewContext = useCallback(() => {
    setNewWizardKey(null);
    setExtendWizardKey(null);
    setIsCreatingNew(false);
    setModalContextId(null);
    setModalMode(null);
    setModalOpen(false);
    setListSelectionId(panelContextId);
  }, [panelContextId]);

  const hideModal = useCallback(() => {
    if (isCreatingNew && modalContextId == null) {
      cancelNewContext();
      return;
    }
    setModalOpen(false);
    setExtendWizardKey(null);
  }, [isCreatingNew, modalContextId, cancelNewContext]);

  const handoffModalToPanel = useCallback((finishedId: number) => {
    setPanelContextId(finishedId);
    setListSelectionId(finishedId);
    setModalOpen(false);
    setModalContextId(null);
    setModalMode(null);
    setNewWizardKey(null);
    setExtendWizardKey(null);
    setIsCreatingNew(false);
  }, []);

  const openAddContextExtend = useCallback(
    async (targetId: number) => {
      const target = contexts.find((c) => c.id === targetId);
      if (!target || target.status !== "completed") return;

      setExtendWizardKey(`extend-${targetId}-${Date.now()}`);
      setListSelectionId(targetId);
      setModalContextId(targetId);
      setModalMode("extend");
      setModalOpen(true);
      setNewWizardKey(null);
      setIsCreatingNew(false);
    },
    [contexts]
  );

  const openModalForContext = useCallback((id: number, mode: ModalMode = "resume") => {
    setExtendWizardKey(null);
    setListSelectionId(id);
    setModalContextId(id);
    setModalMode(mode);
    setModalOpen(true);
    setNewWizardKey(null);
    setIsCreatingNew(false);
  }, []);

  const duplicateContextForExtend = useCallback(
    async (sourceId: number): Promise<number> => {
      if (!project || boardId == null) {
        throw new Error("Project not available");
      }
      if (isDuplicating) {
        throw new Error("Duplicate already in progress");
      }
      setIsDuplicating(true);
      try {
        const newId = await duplicateBoardContextRecords(project, sourceId, Number(boardId));
        return newId;
      } finally {
        setIsDuplicating(false);
      }
    },
    [project, boardId, isDuplicating]
  );

  const finalizeExtendDuplicate = useCallback((newId: number) => {
    setModalContextId(newId);
    setListSelectionId(newId);
    setModalMode("extend");
  }, []);

  const deleteContext = useCallback(
    async (id: number) => {
      if (!project) return;
      const store = project.getCrudStore(STORE_KEYS.boardContextStore);
      const record = store?.getById(id) as unknown as BoardContext | null;
      if (!record) return;

      if (record.isActive === true) {
        showToast({ msg: "The active context cannot be deleted.", type: "error", duration: 4000 });
        return;
      }

      record.remove();
      try {
        await project.sync();
        if (listSelectionId === id || panelContextId === id || modalContextId === id) {
          setNewWizardKey(null);
          setIsCreatingNew(false);
          if (modalContextId === id) {
            setModalOpen(false);
            setModalContextId(null);
            setModalMode(null);
          }
          const remaining = (store.records as unknown as BoardContext[]).filter(
            (r) => r.board === boardId || r.board === Number(boardId)
          );
          const nextPanel = resolveDefaultPanelContextId(remaining);
          setPanelContextId(nextPanel);
          setListSelectionId(nextPanel);
        }
        showToast({ msg: "Context deleted.", type: "success", duration: 3000 });
      } catch {
        showToast({ msg: "Failed to delete context. Please try again.", type: "error", duration: 4000 });
      }
    },
    [project, boardId, listSelectionId, panelContextId, modalContextId]
  );

  const renameContext = useCallback(
    async (id: number, name: string) => {
      if (!project) return;
      const store = project.getCrudStore(STORE_KEYS.boardContextStore);
      const record = store?.getById(id) as unknown as BoardContext | null;
      if (!record) return;
      const trimmed = name.trim();
      if (!trimmed) return;
      record.set({ name: trimmed });
      try {
        await project.sync();
      } catch {
        showToast({ msg: "Failed to rename context. Please try again.", type: "error", duration: 4000 });
      }
    },
    [project]
  );

  const duplicateContext = useCallback(
    async (id: number): Promise<number | null> => {
      if (!project || boardId == null) return null;
      if (isDuplicating) return null;

      const store = project.getCrudStore(STORE_KEYS.boardContextStore);
      const source = store?.getById(id) as unknown as BoardContext | null;
      if (source?.status !== "completed") {
        showToast({ msg: "Only completed contexts can be duplicated.", type: "error", duration: 4000 });
        return null;
      }

      setIsDuplicating(true);
      try {
        const newId = await duplicateBoardContextRecords(project, id, Number(boardId));
        setNewWizardKey(null);
        setIsCreatingNew(false);
        setPanelContextId(newId);
        setListSelectionId(newId);
        setModalOpen(false);
        setModalContextId(null);
        setModalMode(null);
        showToast({ msg: "Context duplicated.", type: "success", duration: 3000 });
        return newId;
      } catch {
        showToast({ msg: "Failed to duplicate context. Please try again.", type: "error", duration: 4000 });
        return null;
      } finally {
        setIsDuplicating(false);
      }
    },
    [project, boardId, isDuplicating]
  );

  const makeActive = useCallback(
    async (id: number) => {
      if (!project) return;
      setIsMakingActive(true);
      try {
        const store = project.getCrudStore(STORE_KEYS.boardContextStore);
        const record = store?.getById(id) as unknown as BoardContext | null;
        if (!record) return;

        if (record.isActive === true) {
          showToast({ msg: "This context is already active.", type: "error", duration: 4000 });
          return;
        }

        record.set({ isActive: true });
        await project.sync();

        showToast({
          msg: "Context set as active. Your Marketing Manifest and tags will update shortly.",
          type: "success",
          duration: 5000,
        });
      } catch {
        showToast({ msg: "Failed to activate context. Please try again.", type: "error", duration: 4000 });
      } finally {
        setIsMakingActive(false);
      }
    },
    [project]
  );

  const value: ContextPageContextValue = {
    contexts,
    activeContext,
    listSelectionId,
    panelContextId,
    modalContextId,
    modalOpen,
    modalMode,
    selectContext,
    deleteContext,
    renameContext,
    makeActive,
    duplicateContext,
    isMakingActive,
    isDuplicating,
    isCreatingNew,
    newWizardKey,
    modalSessionKey,
    beginNewContext,
    openAddContextExtend,
    openModalForContext,
    duplicateContextForExtend,
    finalizeExtendDuplicate,
    hideModal,
    handoffModalToPanel,
    commitNewWizardContext,
    cancelNewContext,
  };

  return <ContextPageCtx.Provider value={value}>{children}</ContextPageCtx.Provider>;
};

export const useContextPage = () => {
  const ctx = useContext(ContextPageCtx);
  if (!ctx) throw new Error("useContextPage must be used within ContextPageProvider");
  return ctx;
};
